import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { IExtensionsWorkbenchService } from 'vscode/vscode/vs/workbench/contrib/extensions/common/extensions.service';
import 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { SearchExtensionsAction } from 'vscode/vscode/vs/workbench/contrib/extensions/browser/extensionsActions';
import { distinct } from 'vscode/vscode/vs/base/common/arrays';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IExtensionManagementService } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionManagement.service';
import { areSameExtensions } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionManagementUtil';
import { IWorkbenchExtensionEnablementService } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionManagement.service';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';

const _moduleId = "vs/workbench/contrib/extensions/browser/deprecatedExtensionsChecker";
let DeprecatedExtensionsChecker = class DeprecatedExtensionsChecker extends Disposable {
    constructor(extensionsWorkbenchService, extensionManagementService, extensionEnablementService, storageService, notificationService, instantiationService) {
        super();
        this.extensionsWorkbenchService = extensionsWorkbenchService;
        this.extensionEnablementService = extensionEnablementService;
        this.storageService = storageService;
        this.notificationService = notificationService;
        this.instantiationService = instantiationService;
        this.checkForDeprecatedExtensions();
        this._register(extensionManagementService.onDidInstallExtensions(e => {
            const ids = [];
            for (const { local } of e) {
                if (local && extensionsWorkbenchService.local.find(extension => areSameExtensions(extension.identifier, local.identifier))?.deprecationInfo) {
                    ids.push(local.identifier.id.toLowerCase());
                }
            }
            if (ids.length) {
                this.setNotifiedDeprecatedExtensions(ids);
            }
        }));
    }
    async checkForDeprecatedExtensions() {
        if (this.storageService.getBoolean('extensionsAssistant/doNotCheckDeprecated', 0 , false)) {
            return;
        }
        const local = await this.extensionsWorkbenchService.queryLocal();
        const previouslyNotified = this.getNotifiedDeprecatedExtensions();
        const toNotify = local.filter(e => !!e.deprecationInfo && e.local && this.extensionEnablementService.isEnabled(e.local)).filter(e => !previouslyNotified.includes(e.identifier.id.toLowerCase()));
        if (toNotify.length) {
            this.notificationService.prompt(Severity$1.Warning, ( localizeWithPath(
                _moduleId,
                0,
                "You have deprecated extensions installed. We recommend to review them and migrate to alternatives."
            )), [{
                    label: ( localizeWithPath(_moduleId, 1, "Show Deprecated Extensions")),
                    run: async () => {
                        this.setNotifiedDeprecatedExtensions(( (toNotify.map(e => e.identifier.id.toLowerCase()))));
                        const action = this.instantiationService.createInstance(SearchExtensionsAction, ( (toNotify.map(extension => `@id:${extension.identifier.id}`))).join(' '));
                        try {
                            await action.run();
                        }
                        finally {
                            action.dispose();
                        }
                    }
                }, {
                    label: ( localizeWithPath(_moduleId, 2, "Don't Show Again")),
                    isSecondary: true,
                    run: () => this.storageService.store('extensionsAssistant/doNotCheckDeprecated', true, 0 , 0 )
                }]);
        }
    }
    getNotifiedDeprecatedExtensions() {
        return JSON.parse(this.storageService.get('extensionsAssistant/deprecated', 0 , '[]'));
    }
    setNotifiedDeprecatedExtensions(notified) {
        this.storageService.store('extensionsAssistant/deprecated', JSON.stringify(distinct([...this.getNotifiedDeprecatedExtensions(), ...notified])), 0 , 0 );
    }
};
DeprecatedExtensionsChecker = ( (__decorate([
    ( (__param(0, IExtensionsWorkbenchService))),
    ( (__param(1, IExtensionManagementService))),
    ( (__param(2, IWorkbenchExtensionEnablementService))),
    ( (__param(3, IStorageService))),
    ( (__param(4, INotificationService))),
    ( (__param(5, IInstantiationService)))
], DeprecatedExtensionsChecker)));

export { DeprecatedExtensionsChecker };
